
import Vue from 'vue';
import { trackContactClick, trackNavigationClick, trackLinkClick } from '~/utility/tracking';

export default Vue.extend({
  props: {
    ariaLabel: {
      type: String,
      default: undefined,
    },
    fontWeightBold: {
      type: Boolean,
      default: true,
    },
    tertiary: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'Link with some label',
    },
    url: {
      type: String,
      default: undefined,
    },
    target: {
      type: String,
      default: undefined,
    },
    icon: {
      type: [String, Boolean],
      required: false,
      default: 'arrow-right',
    },
    iconPosition: {
      type: String,
      required: false,
      default: 'trailing',
    },
    underline: {
      type: Boolean,
      default: false,
    },
    isBreadcrumb: {
      type: Boolean,
      default: false,
    },
    trackingContext: {
      type: Object as () => {
        location: 'main-nav' | 'inline-nav' | 'mobile-menu' | 'service-menu' | 'contact' | 'language' | 'footer-nav';
        category?: string;
      } | null,
      default: null,
    },
    trackingModule: {
      type: String,
      required: false,
    },
  },
  computed: {
    type(): string | undefined {
      if (!this.url) return 'button';
      if (this.leaveMarket) return 'a';
      return this.url && !this.url.includes('://') && !this.url.includes('mailto:') && !this.url.includes('tel:')
        ? 'nuxt-link'
        : 'a';
    },
    href(): string | undefined {
      return this.type === 'a' || this.leaveMarket ? this.url : undefined;
    },
    to(): string | null {
      if (this.isBreadcrumb && this.$store.state.internalRouting) {
        return this.$store.state.internalRouting;
      }
      if (this.type !== 'a') {
        return this.url === '//' ? '/' : this.url;
      }
      return null;
    },
    getLabel(): any {
      return this.$slots.default || this.label;
    },
    leaveMarket(): boolean {
      const routeLocale = this.$route.path.split('/')[1];
      const linkLocale = this.url.split('/')[1];

      if (linkLocale !== routeLocale) return true;
      return false;
    },
  },
  data() {
    return {};
  },
  methods: {
    handleClick(e: PointerEvent): void {
      if (this.handleCookieConsentClick()) {
        e.preventDefault();
        return;
      }
      this.validateLink();
      this.trackContactIfApplicable();
      this.trackNavigationIfApplicable();
      this.trackLinkIfApplicable();
    },

    /**
     * Allows opening the cookie consent banner using '#cookies' as url target.
     */
    handleCookieConsentClick(): boolean {
      if (/#cookies/.test(this.url)) {
        window.UC_UI.showSecondLayer();
        return true;
      }
      return false;
    },

    validateLink(): any {
      if (document.body.classList.contains('body-locked') && !this.url.includes('?showLayer')) {
        document.body.classList.remove('body-locked');
      }
      if (this.to === (this.$route.path.slice(-1) === '/' ? this.$route.path : this.$route.path + '/')) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },

    trackContactIfApplicable(): void {
      if (!this.url) return;

      if (this.url.includes('mailto:')) {
        const email = this.url.replace('mailto:', '');
        trackContactClick('email', email, this.icon ? `icon:${this.icon}` : undefined);
      } else if (this.url.includes('tel:')) {
        const phone = this.url.replace('tel:', '').trim();
        trackContactClick('phone', phone, this.icon ? `icon:${this.icon}` : undefined);
      }
    },

    trackNavigationIfApplicable(): void {
      if (!this.trackingContext || !this.url) return;

      // Don't track mailto: and tel: links as navigation (they're tracked as contact)
      if (this.url.includes('mailto:') || this.url.includes('tel:')) return;

      trackNavigationClick(this.label || '', this.url, this.trackingContext.location, this.trackingContext.category);
    },

    trackLinkIfApplicable(): void {
      if (!this.trackingModule || !this.url) return;

      // Don't track mailto: and tel: links as module links (they're tracked as contact)
      if (this.url.includes('mailto:') || this.url.includes('tel:')) return;

      trackLinkClick(this.label || '', this.url, this.trackingModule);
    },
  },
});
